<template>
  <div class="app-container">

    <!-- MOBILE TOP BAR -->
    <div class="mobile-topbar">
      <button class="menu-btn" @click="toggleSidebar">☰</button>
      <span class="mobile-title">NoCodeAgent</span>
    </div>

    <!-- OVERLAY -->
    <div v-if="sidebarOpen" class="overlay" @click="toggleSidebar"></div>

    <!-- SIDEBAR -->
    <aside :class="['sidebar', { open: sidebarOpen }]">

      <div class="top">
        <h2 class="logo">NoCodeAgent</h2>

        <div class="user-block">
          <div class="avatar">
            {{ userName?.charAt(0) }}
          </div>
          <div class="user-info">
            <div class="user-name">{{ userName }}</div>
            <div class="agent-name">{{ agentName }}</div>
          </div>
        </div>

        <nav class="nav-links">
          <router-link
            v-for="item in menuItems"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            active-class="active"
            @click="closeSidebarMobile"

          >
            <component :is="item.icon" class="icon" />
            <span>{{ $t(item.textCode) }}</span>
          </router-link>
        </nav>
      </div>

      <div class="bottom-actions">
        <button @click="goToAgentSelection">
          <Repeat class="icon" />
          <span>{{ $t('changeAgent') }}</span>
        </button>

        <button @click="createNewAgent">
          <Plus class="icon" />
          <span>{{ $t('createAgent') }}</span>
        </button>

        <button @click="$router.push('/app/plan')">
          <CreditCard class="icon" />
          <span>{{ $t('changePlan') }}</span>
        </button>

        <button @click="logout" class="logout-btn">
          <LogOut class="icon" />
          <span>{{ $t('logout') }}</span>
        </button>
      </div>

    </aside>

    <!-- MAIN -->
    <main class="main-content">
      <router-view />
    </main>

    <GlobalAssistantLauncher
      :agent-id="activeAgentId"
      @agent-applied="refreshActiveAgent"
    />

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Cookies from 'js-cookie'
import * as api from '@/services/api.js'
import GlobalAssistantLauncher from '@/components/GlobalAssistantLauncher.vue'



import router from '@/router'

import {
  LayoutDashboard,
  FlaskConical,
  Phone,
  Calendar,
  Bot,
  Activity,
  BookOpen,
  Brain,
  History,
  Workflow,
  CreditCard,
  Settings,
  LifeBuoy,
  Repeat,
  Plus,
  LogOut
} from 'lucide-vue-next'

const userName = ref('')
const agentName = ref('')
const activeAgentId = ref('')
const sidebarOpen = ref(false)

/* MENU */
const menuItems = [
  { textCode: 'menu.dashboard', path: '/app/dashboard', icon: LayoutDashboard },
  { textCode: 'menu.testLive', path: '/app/test-live', icon: FlaskConical },
  { textCode: 'Telephony', path: '/app/telephony', icon: Phone },
  
  // CORE
  { textCode: 'menu.booking', path: '/app/booking', icon: Calendar },
  { textCode: 'menu.agent', path: '/app/agent', icon: Bot },
  { textCode: 'menu.live-monitor', path: '/app/live-monitor', icon: Activity },
  { textCode: 'menu.knowledge', path: '/app/knowledge', icon: BookOpen },
  { textCode: 'menu.personality', path: '/app/personality', icon: Brain },

  // OPERATIONS
  { textCode: 'menu.history', path: '/app/history', icon: History },
  { textCode: 'menu.workflows', path: '/app/workflows', icon: Workflow },

  // BUSINESS
  { textCode: 'menu.plan', path: '/app/plan', icon: CreditCard },

  // SYSTEM
  { textCode: 'menu.settings', path: '/app/settings', icon: Settings },
  { textCode: 'menu.support', path: '/app/support', icon: LifeBuoy }
]

/* INIT */
onMounted(async () => {
  userName.value = localStorage.getItem('userName') || 'User'

  const agentId = Cookies.get('active_agent_id')
  activeAgentId.value = agentId || ''

  if (!agentId) {
    router.push('/agents')
    return
  }

  await refreshActiveAgent()
})

async function refreshActiveAgent() {
  const agentId = Cookies.get('active_agent_id')
  activeAgentId.value = agentId || ''

  if (!agentId) return

  try {
    const agent = await api.agents.getById(agentId)
    agentName.value = agent.name
  } catch (err) {
    console.warn(err)
  }
}

/* SIDEBAR */
function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
}

function closeSidebarMobile() {
  if (window.innerWidth < 768) {
    sidebarOpen.value = false
  }
}

/* ACTIONS */
function goToAgentSelection() {
  router.push('/agents')
}

function createNewAgent() {
  Cookies.remove('active_agent_id')
  router.push('/create-agent')
}

function logout() {
  localStorage.clear()
  document.cookie = 'token=; Max-Age=0'
  router.push('/sign-in')
}
</script>

<style scoped>
.app-container {
  display: flex;
  height: 100vh;
}

/* MOBILE TOPBAR */
.mobile-topbar {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #020617;
  color: white;
  align-items: center;
  padding: 0 15px;
  z-index: 1000;
}

.menu-btn {
  font-size: 20px;
  margin-right: 10px;
}

/* SIDEBAR */
.sidebar {
  width: 260px;
  background: #020617;
  color: white;
  display: flex;
  flex-direction: column;
  padding: 20px;
  transition: transform 0.3s ease;
}

.logo {
  font-weight: bold;
  margin-bottom: 20px;
}

.user-block {
  margin-bottom: 20px;
  color: #94a3b8;
}

.nav-links {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 20px;
}

.nav-item {
  padding: 10px;
  border-radius: 8px;
  text-decoration: none;
  color: #cbd5f5;
}

.nav-item.active {
  background: #4f46e5;
  color: white;
}

.bottom-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bottom-actions button {
  padding: 10px;
  background: #1e293b;
  border: none;
  color: white;
  border-radius: 8px;
}

/* MAIN */
.main-content {
  flex: 1;
  background: #f9fafb;
  overflow-y: auto;
}

/* OVERLAY */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 900;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .mobile-topbar {
    display: flex;
  }

  .sidebar {
    position: fixed;
    top: 0;
    bottom: 0;
    transform: translateX(-100%);
    z-index: 1001;
  }

  .sidebar.open {
    transform: translateX(0);
  }

  .main-content {
    padding: 80px 20px 20px;
  }
}

.user-block {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.agent-name {
  font-size: 0.75rem;
  color: #94a3b8;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  color: #cbd5f5;
  text-decoration: none;
}

.icon {
  width: 18px;
  height: 18px;
  opacity: 0.8;
}

.nav-item.active {
  background: #4f46e5;
  color: white;
}

.nav-item.active .icon {
  opacity: 1;
}

.bottom-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #1e293b; /* 🔥 séparation propre */
  padding-top: 15px;
}

.bottom-actions button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #1e293b;
  border: none;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
}

.bottom-actions button:hover {
  background: #334155;
}

.logout-btn {
  color: #f87171;
}
</style>
